import React, {ReactElement, ReactNode, useMemo} from 'react';
import format from 'string-format';

import {CelebrationIcon} from '../../../resources/Icons';
import Col from '../../common/Col';
import {useAppDispatch} from '../../store';
import {Actions} from '../gameSlice';
import {useGameStatus} from '../hooks';
import {isHarvestYear, isPlantingYear, shouldBeHarvestYear} from '../mechanics';
import ArrowButton from './buttons/ArrowButton';
import MessageBox from './MessageBox';

type WrapUpMessageId =
  | 'prepareYear'
  | 'highScorePlantingYear'
  | 'midScorePlantingYear'
  | 'lowScorePlantingYear'
  | 'lastYear'
  | 'waitYear';

type ExtraMessageId = 'cantHarvest' | 'harvestYear';

type WrapUpMessage = {
  icon: ReactNode;
  title: string;
  subtitle: string;
};

const WRAP_UP_MESSAGES: Record<WrapUpMessageId, WrapUpMessage | WrapUpMessage[]> = {
  prepareYear: {
    icon: <CelebrationIcon className="h-40 w-40 m-auto" />,
    title: 'Well done!',
    subtitle: 'You decided to spend a year getting your land ready. Proceed to next year to plant.',
  },
  highScorePlantingYear: {
    icon: <CelebrationIcon className="h-40 w-40 m-auto" />,
    title: 'Well done!',
    subtitle: 'You made some good choices and are well on the way.',
  },
  midScorePlantingYear: {
    icon: null,
    title: 'Not bad!',
    subtitle:
      'You cut some corners and saved some costs. That will have an impact on outputs but you can still hit your target.',
  },
  lowScorePlantingYear: {
    icon: null,
    title: 'Dear, oh dear!',
    subtitle: "You've made a bit of a mess of things. It's going to be tough achieving your target from here.",
  },
  lastYear: {
    icon: null,
    title: 'Drum roll, please.',
    subtitle: 'The results are in. Did you make the grade?',
  },
  waitYear: [
    {
      icon: null,
      title: "There's nothing else to do in year {} except watch your crop grow.",
      subtitle: 'You might as well put your feet up have a cup of tea!',
    },
    {
      icon: null,
      title: "There's nothing else to do in year {} except watch your crop grow.",
      subtitle: "Perhaps this year you could set up the farm shop you've always wanted!",
    },
    {
      icon: null,
      title: "There's nothing else to do in year {} except watch your crop grow.",
      subtitle: 'Get the sun lounger out and work on that t-shirt tan!',
    },
    {
      icon: null,
      title: 'There’s nothing else to do in year {}.',
      subtitle: "Maybe it's time to consider setting up a music festival on your farm?",
    },
  ],
};

const EXTRA_MESSAGES: Record<ExtraMessageId, string> = {
  cantHarvest: "Unfortunately, you can't harvest this year due to floods. You need to wait one more year.",
  harvestYear: 'Time to collect your harvest!',
};

const YearWrapUp = (): ReactElement => {
  const dispatch = useAppDispatch();
  const gameStatus = useGameStatus();
  const onHarvestYear = isHarvestYear(gameStatus);
  const isLastYear = gameStatus.currentYear === gameStatus.totalYears - 1;

  const messagesId = useMemo<WrapUpMessageId>(() => {
    if (gameStatus.currentYear === 0 && !isPlantingYear(gameStatus)) return 'prepareYear';
    if (isPlantingYear(gameStatus)) {
      const penaltyPoints = gameStatus.history[gameStatus.currentYear].choices.reduce(
        (score, choice) => score + (choice.option?.penaltyPoints ?? 0),
        0
      );
      if (penaltyPoints <= 1) return 'highScorePlantingYear';
      if (penaltyPoints <= 4) return 'midScorePlantingYear';
      return 'lowScorePlantingYear';
    }
    if (isLastYear) return 'lastYear';
    return 'waitYear';
  }, [gameStatus, isLastYear]);

  const messages = useMemo(() => {
    const currentMessage = WRAP_UP_MESSAGES[messagesId];
    if (Array.isArray(currentMessage)) {
      const msgIndex = gameStatus.currentYear % currentMessage.length;
      const {title, ...others} = currentMessage[msgIndex];
      return {title: format(title, (gameStatus.currentYear + 1).toFixed(0)), ...others};
    }
    return currentMessage;
  }, [gameStatus.currentYear, messagesId]);

  const extraMessage = useMemo(() => {
    if (!onHarvestYear && shouldBeHarvestYear(gameStatus)) return EXTRA_MESSAGES['cantHarvest'];
    if (onHarvestYear) return EXTRA_MESSAGES['harvestYear'];
    return undefined;
  }, [gameStatus, onHarvestYear]);

  return (
    <MessageBox
      bottom={
        <div className="flex justify-center">
          <ArrowButton
            direction="right"
            onClick={() => dispatch(onHarvestYear ? Actions.harvest() : Actions.goToNextYear())}
          >
            {onHarvestYear
              ? 'Harvest'
              : !isLastYear
              ? `Go to year ${gameStatus.currentYear + 2}`
              : 'Check your results'}
          </ArrowButton>
        </div>
      }
    >
      <Col className="space-y-sm text-center py-md px-32 mobile:px-6 mobile:pb-4">
        {messages.icon}
        <p className="text-3xl font-semibold mobile:text-2xl">{messages.title}</p>
        <p className="text-2xl font-semibold mobile:text-lg">{messages.subtitle}</p>
        {!!extraMessage && <p className="text-xl">{extraMessage}</p>}
      </Col>
    </MessageBox>
  );
};

export default YearWrapUp;
